import type { TFunction } from "i18next";
import type { KeyboardActionId } from "~/keyboard/actionCatalog";

import { useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AuthContext } from "~/auth/AuthProvider";
import { isUnrestrictedStaff } from "~/auth/roleChecks";
import useMyUser from "~/auth/useMyUser";
import { exactMatchFirst } from "~/components/select/helpers/optionFilters";
import {
	type CombinedOrderDocumentFilterInput,
	CustomerStatus,
	type CustomersSummaryDocumentFilterInput,
	OrderEntityType
} from "~/gql/graphqlBase.generated";
import {
	type OmniSearchCustomersQuery,
	type OmniSearchItemsQuery,
	type OmniSearchOrdersQuery,
	useLazyOmniSearchCustomersQuery,
	useLazyOmniSearchItemsQuery,
	useLazyOmniSearchOrdersQuery
} from "~/gql/queries/omniSearch.generated";
import addSearchFilter from "~/helpers/ag-grid/addSearchFilter";
import { colorForModule } from "~/helpers/recentDestinations";
import { buildCustomerSearchOr, describeCustomerMatch } from "~/helpers/search/customerSearch";
import parseEntitySearch, { type EntitySearchIntent } from "~/helpers/search/entitySearchQuery";
import useDebounce from "~/hooks/useDebounce";
import useItemSearchSuggestion from "~/hooks/useItemSearchSuggestion";
import useJevCustomerSuggestions from "~/hooks/useJevCustomerSuggestions";
import useJevItemSuggestions from "~/hooks/useJevItemSuggestions";
import { type CustomerSearchMatch, type ItemSearchMatch } from "~/services/aiOrchestrator";
import { SEARCH_FIELDS as ORDER_SEARCH_FIELDS } from "~fulfill/orders/helpers/ordersCombinedFilter";
import { buildItemsRoute } from "~fulfill/product/items/helpers/itemsRoutes";

export interface SearchResultRow {
	color: string;
	icon: string;
	key: string;
	label: string;
	path: string;
	run?: () => void;
	secondary?: string;
	shortcutActionId?: KeyboardActionId;
	state?: { dockTitle: string };
}

export interface SearchSection {
	key: string;
	loading: boolean;
	rows: SearchResultRow[];
	skeletonCount: number;
	title: string;
}

export interface EntitySearchState {
	intent: EntitySearchIntent;
	itemSuggestion: null | string;
	sections: {
		customers: SearchSection | null;
		customerSuggestions: SearchSection | null;
		items: SearchSection | null;
		itemSuggestions: SearchSection | null;
		orders: SearchSection | null;
	};
}

const ENTITY_SEARCH_DEBOUNCE_MS = 200;
const MIN_ENTITY_QUERY_LENGTH = 2;
const EXPLICIT_LIMIT = 6;
const LIGHT_LIMIT = 3;
const SKELETON_COUNT = 3;

const FULFILL_COLOR = colorForModule("fulfill");

interface EntityPlanEntry {
	limit: number;
	term: string;
}

interface EntityPlan {
	customers: EntityPlanEntry | null;
	items: EntityPlanEntry | null;
	orders: EntityPlanEntry | null;
}

const buildPlan = (intent: EntitySearchIntent): EntityPlan => {
	const term = intent.query.trim();
	if (intent.command || term.length < MIN_ENTITY_QUERY_LENGTH) {
		return { customers: null, items: null, orders: null };
	}
	if (intent.entity) {
		return {
			customers: intent.entity === "customers" ? { limit: EXPLICIT_LIMIT, term } : null,
			items: intent.entity === "items" ? { limit: EXPLICIT_LIMIT, term } : null,
			orders: intent.entity === "orders" ? { limit: EXPLICIT_LIMIT, term } : null
		};
	}
	return {
		customers: { limit: LIGHT_LIMIT, term },
		items: { limit: LIGHT_LIMIT, term },
		orders: { limit: intent.isOrderNumber ? EXPLICIT_LIMIT : LIGHT_LIMIT, term }
	};
};

const buildOrderWhere = (term: string): CombinedOrderDocumentFilterInput =>
	(addSearchFilter(term.replace(/^#/, ""), ORDER_SEARCH_FIELDS) ?? {}) as CombinedOrderDocumentFilterInput;

const buildCustomerWhere = (term: string): CustomersSummaryDocumentFilterInput => ({
	or: buildCustomerSearchOr(term),
	status: { eq: CustomerStatus.Active }
});

const toOrderRows = (
	data: OmniSearchOrdersQuery | undefined,
	term: string,
	limit: number,
	t: TFunction<"common">
): SearchResultRow[] => {
	const nodes = data?.combinedOrders?.nodes ?? [];
	return exactMatchFirst(nodes, term.replace(/^#/, ""), (node) => [node.orderEntityNumber])
		.slice(0, limit)
		.map((node) => {
			const isMaster = node.orderEntityType === OrderEntityType.MasterOrder;
			const dockTitle = isMaster
				? t("search.rows.masterOrder", { number: node.orderEntityNumber })
				: t("search.rows.order", { number: node.orderEntityNumber });
			return {
				color: FULFILL_COLOR,
				icon: "receipt_long",
				key: `order-${node.orderEntityId}`,
				label: isMaster ? dockTitle : `#${node.orderEntityNumber}`,
				path: `/fulfill/orders/${isMaster ? "master-order" : "order"}/${node.orderEntityId}`,
				secondary: node.customerName || undefined,
				state: { dockTitle }
			};
		});
};

const customerCodeLine = (
	erpId: null | string | undefined,
	lookupCode: null | string | undefined,
	t: TFunction<"common">
) => (erpId ? `ERP ${erpId}` : lookupCode ? t("search.rows.code", { code: lookupCode }) : undefined);

const toCustomerRows = (
	data: OmniSearchCustomersQuery | undefined,
	term: string,
	limit: number,
	t: TFunction<"common">
): SearchResultRow[] =>
	(data?.customers?.nodes ?? []).slice(0, limit).map((node) => {
		const match = describeCustomerMatch(node, term);
		return {
			color: FULFILL_COLOR,
			icon: "storefront",
			key: `customer-${node.customerId}`,
			label: node.name,
			path: `/fulfill/customers/customer/${node.customerId}`,
			secondary: match
				? match.kind === "contact"
					? t("search.rows.contact", { name: match.text })
					: match.text
				: customerCodeLine(node.erpId, node.customerLookupCode, t),
			state: { dockTitle: node.name }
		};
	});

const toJevCustomerRows = (matches: CustomerSearchMatch[], t: TFunction<"common">): SearchResultRow[] =>
	matches.map((match) => ({
		color: FULFILL_COLOR,
		icon: "storefront",
		key: `jev-customer-${match.customerId}`,
		label: match.name,
		path: `/fulfill/customers/customer/${match.customerId}`,
		secondary: customerCodeLine(match.erpId, match.lookupCode, t),
		state: { dockTitle: match.name }
	}));

const toItemRows = (data: OmniSearchItemsQuery | undefined, limit: number): SearchResultRow[] =>
	(data?.items?.nodes ?? []).slice(0, limit).map((node) => ({
		color: FULFILL_COLOR,
		icon: "inventory_2",
		key: `item-${node.itemId}`,
		label: node.name,
		path: buildItemsRoute({ itemId: node.itemId, mode: "detail" }),
		secondary: node.sku ? `SKU ${node.sku}` : node.upc ? `UPC ${node.upc}` : undefined
	}));

const toJevItemRows = (matches: ItemSearchMatch[]): SearchResultRow[] =>
	matches.map((match) => ({
		color: FULFILL_COLOR,
		icon: "inventory_2",
		key: `jev-item-${match.itemId}`,
		label: match.name,
		path: buildItemsRoute({ itemId: match.itemId, mode: "detail" }),
		secondary: `SKU ${match.sku}`
	}));

const buildSection = (
	key: string,
	title: string,
	requested: EntityPlanEntry | null,
	fetched: EntityPlanEntry | null,
	isFetching: boolean,
	rows: SearchResultRow[],
	hasSuggestions: boolean
): SearchSection | null => {
	if (!requested) {
		return null;
	}
	const isCurrent = fetched?.term === requested.term && fetched?.limit === requested.limit;
	const showSkeletons = rows.length === 0 && (isFetching || !isCurrent);
	if (rows.length === 0 && !showSkeletons && !hasSuggestions) {
		return null;
	}
	return { key, loading: showSkeletons, rows, skeletonCount: SKELETON_COUNT, title };
};

const useEntitySearch = (rawQuery: string, active: boolean): EntitySearchState => {
	const { t } = useTranslation("common");
	const authContext = useContext(AuthContext);
	const { myUser } = useMyUser();
	const intent = useMemo(() => parseEntitySearch(rawQuery), [rawQuery]);
	const requested = useMemo(() => buildPlan(intent), [intent]);

	const debouncedRaw = useDebounce(active ? rawQuery : "", ENTITY_SEARCH_DEBOUNCE_MS);
	const fetched = useMemo(() => buildPlan(parseEntitySearch(debouncedRaw)), [debouncedRaw]);

	const [triggerOrders, ordersResult] = useLazyOmniSearchOrdersQuery();
	const [triggerCustomers, customersResult] = useLazyOmniSearchCustomersQuery();
	const [triggerItems, itemsResult] = useLazyOmniSearchItemsQuery();

	const ordersPlan = fetched.orders;
	const customersPlan = fetched.customers;
	const itemsPlan = fetched.items;

	useEffect(() => {
		if (!active || !ordersPlan) return;
		triggerOrders({ first: ordersPlan.limit, where: buildOrderWhere(ordersPlan.term) }, true);
	}, [active, ordersPlan?.term, ordersPlan?.limit]);

	useEffect(() => {
		if (!active || !customersPlan) return;
		triggerCustomers({ first: customersPlan.limit, where: buildCustomerWhere(customersPlan.term) }, true);
	}, [active, customersPlan?.term, customersPlan?.limit]);

	useEffect(() => {
		if (!active || !itemsPlan) return;
		triggerItems({ first: itemsPlan.limit, search: itemsPlan.term }, true);
	}, [active, itemsPlan?.term, itemsPlan?.limit]);

	const settledItemNodes =
		!!itemsPlan && requested.items?.term === itemsPlan.term && itemsResult.originalArgs?.search === itemsPlan.term
			? itemsResult.currentData?.items?.nodes
			: undefined;
	const itemSuggestion = useItemSearchSuggestion({
		search: itemsPlan?.term ?? "",
		skip: settledItemNodes?.length !== 0
	});
	const shownItemIds = useMemo(() => (settledItemNodes ?? []).map((node) => node.itemId), [settledItemNodes]);
	const jevItemSuggestions = useJevItemSuggestions({
		search: requested.items?.term ?? "",
		shownItemIds,
		skip: !settledItemNodes
	});

	const settledCustomerNodes = customersPlan ? customersResult.currentData?.customers?.nodes : undefined;
	const shownCustomerIds = useMemo(
		() => (settledCustomerNodes ?? []).map((node) => node.customerId),
		[settledCustomerNodes]
	);
	const jevCustomerSuggestions = useJevCustomerSuggestions({
		search: requested.customers?.term ?? "",
		shownCustomerIds,
		skip: !settledCustomerNodes || !isUnrestrictedStaff(authContext.currentUser, myUser?.roles)
	});

	const orderRows = useMemo(
		() => toOrderRows(ordersResult.data, ordersPlan?.term ?? "", requested.orders?.limit ?? EXPLICIT_LIMIT, t),
		[ordersResult.data, ordersPlan?.term, requested.orders?.limit, t]
	);
	const customerRows = useMemo(
		() => toCustomerRows(customersResult.data, customersPlan?.term ?? "", requested.customers?.limit ?? LIGHT_LIMIT, t),
		[customersResult.data, customersPlan?.term, requested.customers?.limit, t]
	);
	const itemRows = useMemo(
		() => toItemRows(itemsResult.data, requested.items?.limit ?? LIGHT_LIMIT),
		[itemsResult.data, requested.items?.limit]
	);
	const jevItemRows = useMemo(() => toJevItemRows(jevItemSuggestions), [jevItemSuggestions]);
	const jevCustomerRows = useMemo(() => toJevCustomerRows(jevCustomerSuggestions, t), [jevCustomerSuggestions, t]);

	return useMemo<EntitySearchState>(
		() => ({
			intent,
			itemSuggestion,
			sections: {
				customers: buildSection(
					"customers",
					t("search.sections.customers"),
					requested.customers,
					fetched.customers,
					customersResult.isFetching,
					customerRows,
					jevCustomerRows.length > 0
				),
				customerSuggestions: jevCustomerRows.length
					? {
							key: "customerSuggestions",
							loading: false,
							rows: jevCustomerRows,
							skeletonCount: 0,
							title: t("search.sections.suggestions")
						}
					: null,
				items: buildSection(
					"items",
					t("search.sections.items"),
					requested.items,
					fetched.items,
					itemsResult.isFetching,
					itemRows,
					false
				),
				itemSuggestions: jevItemRows.length
					? {
							key: "itemSuggestions",
							loading: false,
							rows: jevItemRows,
							skeletonCount: 0,
							title: t("search.sections.suggestions")
						}
					: null,
				orders: buildSection(
					"orders",
					t("search.sections.orders"),
					requested.orders,
					fetched.orders,
					ordersResult.isFetching,
					orderRows,
					false
				)
			}
		}),
		[
			intent,
			requested,
			fetched,
			customersResult.isFetching,
			itemsResult.isFetching,
			ordersResult.isFetching,
			customerRows,
			itemRows,
			jevCustomerRows,
			itemSuggestion,
			jevItemRows,
			orderRows,
			t
		]
	);
};

export default useEntitySearch;
